/**
 * @fileoverview A program viewer's home: the programs an organization has
 * shared with them, and what they may see in each.
 *
 * A viewer (a sponsor, a school coordinator) signs in at the org portal but
 * runs nothing, so this is the whole of their portal. What each program shows
 * is decided per grant on the server (`/org/viewer/*`); this page only lists
 * the doors. Each program carries the organization's logo, as its page does.
 *
 * A viewer with one program never sees a list of one: this page opens it.
 */

import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { OrgAuthContext } from "@/App";
import orgApi from "@/lib/orgApi";
import { Card, PageShell, ListRow, Tag } from "@/components/studio/primitives";
import { permissionLabels } from "@/components/org/AddViewerDialog";
import { OrgLogoTile, orgIdentity } from "@/components/org/OrgLayout";

export default function OrgViewerHomePage() {
  const navigate = useNavigate();
  const { org } = useContext(OrgAuthContext) || {};
  const { name: orgName, logoUrl } = orgIdentity(org);
  const [programs, setPrograms] = useState(null);

  useEffect(() => {
    orgApi
      .get("/org/viewer/programs")
      .then((res) => {
        const list = res.data?.programs || [];
        // One program is the whole portal: open it rather than a list of one.
        if (list.length === 1) navigate(`/org/viewer/programs/${list[0].program_id}`, { replace: true });
        else setPrograms(list);
      })
      .catch(() => setPrograms({ failed: true }));
  }, [navigate]);

  return (
    <PageShell width="reading">
      <h1 className="font-serif text-2xl font-semibold text-[var(--text-primary)]">Your programs</h1>
      <p className="mb-6 mt-1.5 font-sans text-[14px] text-[var(--text-secondary)]">
        The programs {org ? orgName : "the organization"} has shared with you.
      </p>
      <Card padding={0} data-testid="viewer-programs">
        {!programs ? (
          <div className="px-[18px] py-6 font-sans text-[15px] text-[var(--text-tertiary)]">Loading…</div>
        ) : programs.failed ? (
          <div className="px-[18px] py-6 font-sans text-[15px] text-[var(--text-tertiary)]">Couldn&rsquo;t load your programs.</div>
        ) : programs.length === 0 ? (
          <div className="px-[18px] py-6 font-sans text-[15px] text-[var(--text-tertiary)]" data-testid="viewer-programs-empty">
            No programs have been shared with you yet.
          </div>
        ) : (
          programs.map((p, i) => (
            <ListRow
              key={p.program_id}
              borderBottom={i < programs.length - 1}
              data-testid={`viewer-program-${p.program_id}`}
              leading={logoUrl ? <OrgLogoTile logoUrl={logoUrl} className="h-10 w-10" testId={`viewer-program-logo-${p.program_id}`} /> : undefined}
              title={p.title}
              subtitle={permissionLabels(p.permissions).join(", ") || "Emails only"}
              wrapText
              trailing={p.permissions.length ? <Tag>{p.permissions.length} shared</Tag> : undefined}
              onPress={() => navigate(`/org/viewer/programs/${p.program_id}`)}
            />
          ))
        )}
      </Card>
    </PageShell>
  );
}
